<template>
  <div class="van-select" :class="{ 'is-inline': inline }" @click="handleOpen">
    <div class="title">{{ displayText }}</div>
    <i class="icon-arrow-down-s-line t-iconfont"></i>
  </div>

  <van-popup v-model:show="showPicker" destroy-on-close position="bottom" round teleport="body">
    <div class="safe-area-inset-bottom van-select-panel">
      <div class="header">
        <div class="left" @click="handleClear">清空</div>
        <div class="title">{{ title }}</div>
        <div class="right" @click="handleConfirm">确认</div>
      </div>
      <div class="cut-line"></div>
      <div class="content">
        <template v-for="(item, index) in props.options" :key="`${item.value}-${index}`">
          <div class="option-row" @click="handleToggle(item)">
            <span>{{ item.label }}</span>
            <van-checkbox
              checked-color="#7a52f4"
              icon-size="16px"
              :model-value="isChecked(item.value)"
            />
          </div>
          <div v-if="index !== props.options.length - 1" class="sub-cut-line"></div>
        </template>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

defineOptions({
  name: 'VanSelect'
})

const model = defineModel({
  type: [Array, String, Number],
  default: () => []
})

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: '请选择'
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  inline: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['change'])

const showPicker = ref(false)
const tempValue = ref([])

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value.slice()
  }
  if (value === null || value === undefined || value === '') {
    return []
  }
  return [value]
}

const currentValue = computed(() => normalizeArray(model.value))

const checkedValues = computed(() =>
  props.multiple && showPicker.value ? tempValue.value : currentValue.value
)

const isChecked = (value) => checkedValues.value.includes(value)

const displayText = computed(() => {
  if (currentValue.value.length === 0) {
    return props.placeholder
  }

  const text = props.options
    .filter((item) => currentValue.value.includes(item.value))
    .map((item) => item.label)
    .filter(Boolean)
    .join('、')

  return text || props.placeholder
})

const handleOpen = () => {
  tempValue.value = currentValue.value
  showPicker.value = true
}

const handleClear = () => {
  const nextValue = props.multiple ? [] : ''
  tempValue.value = []
  model.value = nextValue
  emit('change', nextValue)
  showPicker.value = false
}

const handleToggle = (item) => {
  const value = item.value

  if (!props.multiple) {
    model.value = value
    tempValue.value = [value]
    emit('change', value)
    showPicker.value = false
    return
  }

  const nextValues = normalizeArray(tempValue.value)
  const index = nextValues.indexOf(value)

  if (index > -1) {
    nextValues.splice(index, 1)
  } else {
    nextValues.push(value)
  }

  tempValue.value = nextValues
}

const handleConfirm = () => {
  if (props.multiple) {
    model.value = tempValue.value.slice()
  }
  emit('change', model.value)
  showPicker.value = false
}

watch(
  () => model.value,
  (val) => {
    tempValue.value = normalizeArray(val)
  },
  { immediate: true, deep: true }
)
</script>

<style lang="scss" scoped>
.van-select {
  flex: 0 0 auto;
  width: 132px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f7f8fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: PingFang SC;
  font-size: 14px;
  line-height: 20px;
  color: #323233;
  margin-left: 10px;
  margin-bottom: 8px;

  &.is-inline {
    margin-left: 0;
    margin-bottom: 0;
  }

  .title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.van-select-panel {
  height: 450px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .header {
    flex: 0 0 auto;
    height: 48px;
    margin: 0 16px;
    padding: 10px 0;
    display: flex;
    align-items: center;

    .left {
      flex: 0 0 60px;
      text-align: left;
      font-size: 14px;
      color: #969799;
    }

    .title {
      flex: 1;
      text-align: center;
      font-family: PingFang SC;
      font-size: 16px;
      font-weight: 600;
      color: #323233;
    }

    .right {
      flex: 0 0 60px;
      text-align: right;
      font-size: 14px;
      color: #7a52f4;
    }
  }

  .cut-line {
    flex: 0 0 auto;
    margin: 0 16px;
    height: 1px;
    background-color: #e5e5e5;
    transform: scaleY(0.5);
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    overflow-y: auto;

    .option-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      font-size: 14px;
      color: #323233;
    }

    .sub-cut-line {
      height: 1px;
      background-color: #e5e5e5;
      transform: scaleY(0.5);
    }

    :deep(.van-checkbox) {
      padding: 0;
    }
  }
}
</style>
